import { useMutation } from "@tanstack/react-query";

import { manifestBuilderApi } from "src/api/endpoints";
import {
  DocumentationEntry,
  OnePasswordLinkEntry,
  PromoteFlowVersionRequest,
} from "src/clients/flow-api";
import { ToastParams } from "src/design-system/Toast/constants";
import { toastActions } from "src/design-system/Toast/utils";
import { ProviderType } from "src/flowContainer/ManifestPromotion/types";
import { errorMessage } from "src/utils/stringUtils";

type PromoteFlowVersionParams = {
  flowVersionId: string;
  providerType: ProviderType;
  existingProviderName?: string;
  newProviderDescription?: string;
  onePasswordLinks?: OnePasswordLinkEntry[];
  documentations?: DocumentationEntry[];
  errorHandling?: string;
  comments?: string;
  userEmail: string;
};

export const usePromoteFlowVersionToManifest = () => {
  return useMutation({
    throwOnError: false,
    mutationFn: async ({
      flowVersionId,
      providerType,
      existingProviderName,
      newProviderDescription,
      onePasswordLinks,
      documentations,
      errorHandling,
      comments,
      userEmail,
    }: PromoteFlowVersionParams) => {
      const toastId = toastActions.loading({
        title: "Promoting flow version to manifest",
        description:
          "Please wait while we promote the flow version to a manifest",
      });
      const requestData: PromoteFlowVersionRequest = {
        flow_version_id: flowVersionId,
        provider_type: providerType,
        existing_provider_name: existingProviderName,
        new_provider_description: newProviderDescription,
        one_password_links: onePasswordLinks ?? [],
        documentations: documentations ?? [],
        error_handling: errorHandling,
        comments,
        user_email: userEmail,
      };
      const response =
        await manifestBuilderApi.promoteFlowVersionToManifestApiV2ManifestBuilderPromotePost(
          requestData,
        );
      toastActions.dismiss(toastId);
      return response.data;
    },
    onSuccess: (data) => {
      const params: ToastParams = {
        title: "Flow promoted successfully",
        description: `Provider: ${data.provider_name} | Branch: ${data.branch_name}`,
      };
      toastActions.success(params);
    },
    onError: (error: Error) => {
      toastActions.failure({
        title: "Failed to promote flow version",
        description: errorMessage(error),
        actionText: "Contact Support",
        onActionClick: () => {
          window.open(
            "mailto:support@taktile.com",
            "_blank",
            "noopener noreferrer",
          );
        },
      });
    },
  });
};

type UploadManifestParams = {
  flowVersionId: string;
};

export const useUploadManifest = () => {
  return useMutation({
    throwOnError: false,
    mutationFn: async ({ flowVersionId }: UploadManifestParams) => {
      const toastId = toastActions.loading({
        title: "Uploading manifest",
        description: "Please wait while we upload the manifest",
      });
      const response =
        await manifestBuilderApi.uploadManifestApiV2ManifestBuilderUploadPost({
          flow_version_id: flowVersionId,
        });
      toastActions.dismiss(toastId);
      return response.data;
    },
    onSuccess: () => {
      const params: ToastParams = {
        title: "Manifest uploaded successfully",
      };
      toastActions.success(params);
    },
    onError: (error: Error) => {
      toastActions.failure({
        title: "Failed to upload manifest",
        description: errorMessage(error),
        actionText: "Contact Support",
        onActionClick: () => {
          window.open(
            "mailto:support@taktile.com",
            "_blank",
            "noopener noreferrer",
          );
        },
      });
    },
  });
};
